import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Loader2 } from "lucide-react";
import { ReconRow, STATUS } from "@/components/reconciliation/ReconRow";
import { ImportHistory } from "@/components/reconciliation/ImportHistory";

const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;

export default function Reconciliation() {
  const qc = useQueryClient();
  const [pv, setPv] = useState(null);
  const [dec, setDec] = useState({});
  const [filter, setFilter] = useState("problemi");
  const [busy, setBusy] = useState(false);

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const d = (await api.post("/reconciliation/preview", fd)).data;
      setPv(d); setDec({}); setFilter("problemi");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore lettura file"); }
    finally { setBusy(false); }
  }

  async function changeMonth(m) {
    if (!m) return;
    if (Object.keys(dec).length && !window.confirm("Cambiando mese le scelte fatte riga per riga verranno azzerate. Continuare?")) return;
    const fd = new FormData(); fd.append("month", m);
    try { setPv((await api.post(`/reconciliation/preview/${pv.id}/month`, fd)).data); setDec({}); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  }

  async function commit() {
    setBusy(true);
    try {
      const r = (await api.post(`/reconciliation/preview/${pv.id}/commit`, { decisions: dec })).data;
      const s = r.stats;
      toast.success(`Importazione salvata: ${s.donazioni_aggiunte} donazioni, ${s.donatori_creati} nuovi donatori, ${s.importi_corretti} correzioni`);
      setPv(null);
      ["recon-imports", "donors", "payments", "donations", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  }

  const rows = pv?.rows || [];
  const counts = useMemo(() => rows.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {}), [rows]);
  const shown = rows.filter((r) => filter === "tutte" || (filter === "problemi" ? ["importo_diverso", "dubbio"].includes(r.status) : r.status === filter));
  const actionOf = (r) => dec[r.row]?.action || r.action;
  const total = rows.filter((r) => ["crea", "aggiungi", "collega", "correggi"].includes(actionOf(r))).reduce((s, r) => s + r.amount, 0);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1300px] mx-auto" data-testid="recon-page">
      <PageHeader title="Riconciliazione mensile" subtitle="Carica l'Excel del mese (COGNOME, NOME, CF, importo): controllo donatori e donazioni già registrate prima di salvare."/>

      {!pv && (
        <Card className="p-8 border-2 border-dashed border-stone-300 rounded-xl shadow-none text-center mb-8">
          <label className="cursor-pointer inline-flex items-center gap-2 rounded-full bg-[#1C352D] hover:bg-[#264A3E] text-white px-6 h-11 text-sm transition-colors duration-150">
            {busy ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>} {busy ? "Analisi in corso…" : "Carica file Excel del mese"}
            <input type="file" accept=".xlsx" className="hidden" onChange={onFile} disabled={busy} data-testid="recon-file-input"/>
          </label>
          <div className="text-[12px] text-stone-500 mt-3">Nessun dato viene salvato finché non confermi.</div>
        </Card>
      )}

      {pv && (
        <Card className="border border-stone-200 rounded-xl shadow-none mb-8" data-testid="recon-preview">
          <div className="p-4 border-b border-stone-100 flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="font-medium text-[#1C352D]">{pv.filename}</div>
              <div className="text-[11px] text-stone-500">Foglio {pv.sheet} · {rows.length} righe{pv.detected_month ? ` · mese letto dal file: ${pv.detected_month}` : ""}</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500">Mese donazioni</label>
              <Input type="month" value={pv.month} onChange={(e) => changeMonth(e.target.value)} className="mt-1 w-44" data-testid="recon-month"/>
            </div>
          </div>
          <div className="p-4 flex flex-wrap gap-2 border-b border-stone-100" data-testid="recon-filters">
            {[["problemi", "Da verificare", (counts.importo_diverso || 0) + (counts.dubbio || 0)], ...Object.keys(STATUS).map((k) => [k, STATUS[k].label, counts[k] || 0]), ["tutte", "Tutte", rows.length]].map(([k, l, n]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`recon-filter-${k}`}
                className={`px-3 h-8 rounded-full text-xs border transition-colors duration-150 ${filter === k ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 hover:border-[#1C352D]"}`}>{l} · {n}</button>
            ))}
          </div>
          <div className="max-h-[60vh] overflow-auto">
            {shown.length === 0 ? <div className="p-8 text-center text-sm text-stone-400">Nessuna riga in questa categoria.</div>
              : shown.map((r) => <ReconRow key={r.row} r={r} month={pv.month} decision={dec[r.row]} onChange={(d) => setDec({ ...dec, [r.row]: d })}/>)}
          </div>
          <div className="p-4 border-t border-stone-100 flex flex-wrap items-center gap-3">
            <div className="text-sm flex-1">Da registrare: <b data-testid="recon-total">{eur(total)}</b></div>
            <Button variant="outline" onClick={() => { api.delete(`/reconciliation/preview/${pv.id}`).catch(() => {}); setPv(null); }} data-testid="recon-cancel">Annulla</Button>
            <Button onClick={commit} disabled={busy} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="recon-commit">{busy ? "Salvataggio…" : "Conferma importazione"}</Button>
          </div>
        </Card>
      )}

      <ImportHistory/>
    </div>
  );
}
